import { useEffect, useRef, useCallback } from 'react';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useNotificationStore } from '../store/notificationStore';
import { getAccessToken as getAuthToken } from '../store/tokenAccess';
import type { QueueEvent, BlockedFieldNotification, Notification } from '@/types';

const WS_URL = import.meta.env.VITE_WS_URL || '/ws';

/** Человекочитаемый текст события очереди для тоста/списка уведомлений. */
function queueEventText(event: QueueEvent): string {
  const venue = event.venueName ?? 'очередь';
  switch (event.action) {
    case 'JOIN':
      return `Вы встали в очередь «${venue}». Позиция: ${event.position ?? '?'}.`;
    case 'LEAVE':
      return `Вы покинули очередь «${venue}».`;
    case 'POSITION_CHANGED':
      return `Новая позиция в очереди «${venue}»: ${event.position ?? '?'}.`;
    case 'EVICTED':
      return `Вас удалили из очереди «${venue}».`;
    default:
      return `Событие очереди: ${event.action ?? ''}`;
  }
}

interface UseWebSocketOptions {
  onQueueEvent?: (event: QueueEvent) => void;
  onBlockedField?: (notification: BlockedFieldNotification) => void;
  onNotification?: (notification: Notification) => void;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const clientRef = useRef<Client | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { setConnected, addNotification } = useNotificationStore();

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token) return;

    const sockjs = new SockJS(WS_URL);
    const client = new Client({
      webSocketFactory: () => sockjs,
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = () => {
      setConnected(true);
      console.log('[WebSocket] Connected');

      // Бэкенд (notification-fitness) шлёт через convertAndSendToUser(userId, "/queue/notifications", ...),
      // т.е. клиент получает на /user/queue/notifications. QueueEvent + BlockedField идут
      // в одну точку; различаем по полю action/type.
      client.subscribe('/user/queue/notifications', (message: IMessage) => {
        try {
          const payload = JSON.parse(message.body) as Record<string, unknown>;
          const action = payload.action as string | undefined;
          const type = payload.type as string | undefined;

          if (action) {
            // Событие очереди (JOIN/LEAVE/POSITION_CHANGED/EVICTED).
            const event = payload as unknown as QueueEvent;
            options.onQueueEvent?.(event);
            addNotification({
              id: `queue-${Date.now()}`,
              userId: '',
              type: 'QUEUE_UPDATE',
              title: 'Очередь',
              message: queueEventText(event),
              data: event as unknown as Record<string, unknown>,
              read: false,
              createdAt: new Date().toISOString(),
            });
          } else if (type === 'BLOCKED_FIELD') {
            const notification = payload as unknown as BlockedFieldNotification;
            options.onBlockedField?.(notification);
            addNotification({
              id: `blocked-${Date.now()}`,
              userId: '',
              type: 'WARNING',
              title: 'Поле скрыто',
              message: `${notification.fieldName ?? 'Поле'} было заблокировано: ${notification.reason ?? ''}`,
              data: notification as unknown as Record<string, unknown>,
              read: false,
              createdAt: new Date().toISOString(),
            });
          } else {
            // Произвольное уведомление.
            const notification = payload as unknown as Notification;
            options.onNotification?.(notification);
            addNotification({
              id: `notif-${Date.now()}`,
              userId: '',
              type: 'INFO',
              title: (notification.title as string) ?? 'Уведомление',
              message:
                typeof notification.message === 'string'
                  ? notification.message
                  : JSON.stringify(payload),
              data: payload,
              read: false,
              createdAt: new Date().toISOString(),
            });
          }
        } catch (e) {
          console.error('[WebSocket] Failed to parse notification:', e);
        }
      });
    };

    client.onDisconnect = () => {
      setConnected(false);
      console.log('[WebSocket] Disconnected');
    };

    client.onStompError = (frame) => {
      console.error('[WebSocket] STOMP error:', frame.headers['message'], frame.body);
    };

    client.activate();
    clientRef.current = client;
  }, [setConnected, addNotification, options]);

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.deactivate();
      clientRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    setConnected(false);
  }, [setConnected]);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const sendMessage = useCallback((destination: string, body: object) => {
    clientRef.current?.publish({
      destination,
      body: JSON.stringify(body),
    });
  }, []);

  return { connect, disconnect, sendMessage, isConnected: clientRef.current?.connected ?? false };
}

export function useQueueWebSocket(venueId: number, onUpdate: (event: QueueEvent) => void) {
  useWebSocket({
    onQueueEvent: (event) => {
      if (event.venueId === venueId) {
        onUpdate(event);
      }
    },
  });
}